/**
 * Plano de contas da Apen Capital.
 *
 * A empresa e uma consultoria de investimentos independente regulada pela CVM e
 * um multi-family office: a estrutura de custo e a de uma casa de servicos
 * profissionais regulada — gente, tecnologia e dados de mercado, conformidade
 * regulatoria — e nao a de uma operacao com estoque ou ponto de venda.
 *
 * A lista anterior era de um estudio de pilates ("Head Coach", "Radio
 * Listenx", "Royalties"), herdada do codigo de origem do sistema.
 *
 * Os grupos vivem aqui, junto das categorias, e nao na tela do DRE: com as duas
 * listas separadas, incluir uma categoria sem lembrar de encaixa-la no grupo a
 * fazia sumir do demonstrativo.
 */

export interface GrupoDeContas {
  nome: string;
  /** `receita` entra positivo no extrato; `despesa`, negativo. */
  natureza: 'receita' | 'despesa';
  categorias: readonly string[];
}

export const PLANO_DE_CONTAS: readonly GrupoDeContas[] = [
  {
    nome: 'Receita de serviços',
    natureza: 'receita',
    categorias: [
      'Consultoria de investimentos',
      'Planejamento financeiro',
      'Planejamento sucessório',
      'Estruturação de holding patrimonial',
      'Estruturação offshore',
      'Estudos imobiliários',
      'Tesouraria PJ',
      'Intermediação de crédito',
      'Câmbio e remessas internacionais',
      'Corretagem de seguros',
    ],
  },
  {
    nome: 'Outras receitas',
    natureza: 'receita',
    categorias: [
      // Comissao que a Apen recebe das gestoras. Parte dela volta ao cliente
      // como cashback, lancado do lado da despesa — os dois lados aparecem
      // separados de proposito, para a margem real do modelo ficar visivel.
      'Rebates e comissões de produtos',
      'Receitas financeiras',
      'Reembolsos recebidos',
      'Outras receitas',
    ],
  },

  {
    nome: 'Repasse a clientes',
    natureza: 'despesa',
    categorias: ['Cashback de taxas repassado'],
  },
  {
    nome: 'Pessoal',
    natureza: 'despesa',
    categorias: [
      'Salários - Consultoria',
      'Salários - Operações',
      'Salários - Inteligência',
      'Salários - Planejamento patrimonial',
      'Salários - Administrativo',
      'Pró-labore',
      'Distribuição a sócios',
      'Comissões de consultores',
      'Bonificação e premiação',
      '13º salário',
      'Férias',
      'Rescisões',
      'INSS',
      'FGTS',
      'Vale transporte',
      'Vale refeição e alimentação',
      'Plano de saúde e odontológico',
      'Seguro de vida - colaboradores',
      'Estágio e jovem aprendiz',
      'Medicina do trabalho',
      'Outros - Pessoal',
    ],
  },
  {
    nome: 'Formação e certificação',
    natureza: 'despesa',
    categorias: [
      // Certificacao nao e treinamento qualquer: CFP, CGA e CEA sao exigencia
      // para a atividade regulada, e o custo recorre por profissional.
      'Certificações (CFP, CGA, CEA, CPA)',
      'Treinamento e desenvolvimento',
      'Congressos e cursos',
      'Assinaturas técnicas e publicações',
    ],
  },
  {
    nome: 'Tecnologia e dados',
    natureza: 'despesa',
    categorias: [
      'Plataforma de consolidação patrimonial',
      'Dados de mercado e terminais',
      'Research contratado',
      'CRM e sistemas de gestão',
      'Licenças de software',
      'Hospedagem e nuvem',
      'Suporte e infraestrutura de TI',
      'Telefonia e internet',
      'Outros - Tecnologia',
    ],
  },
  {
    nome: 'Regulatório e conformidade',
    natureza: 'despesa',
    categorias: [
      'Taxa de fiscalização CVM',
      'Anuidade ANBIMA',
      'Auditoria independente',
      'Compliance e prevenção à lavagem',
      'Consultoria regulatória',
      'Seguro de responsabilidade civil',
      'Outros - Regulatório',
    ],
  },
  {
    nome: 'Ocupação',
    natureza: 'despesa',
    categorias: [
      'Aluguel',
      'Condomínio',
      'IPTU',
      'Energia elétrica',
      'Água',
      'Manutenção e reparos',
      'Limpeza e conservação',
      'Segurança e monitoramento',
      'Estacionamento',
      'Copa e consumo',
      'Material de escritório',
      'Outros - Ocupação',
    ],
  },
  {
    nome: 'Comercial e marketing',
    natureza: 'despesa',
    categorias: [
      'Marketing e publicidade',
      'Eventos e patrocínios',
      'Produção de conteúdo',
      'Assessoria de imprensa',
      'Relacionamento com clientes',
      'Comissão de parceiros e originação',
      'Viagens e hospedagem',
      'Brindes institucionais',
      'Outros - Comercial',
    ],
  },
  {
    nome: 'Serviços profissionais',
    natureza: 'despesa',
    categorias: [
      'Contabilidade',
      'Jurídico',
      'Consultorias externas',
      'Recrutamento e seleção',
      'Outros - Serviços profissionais',
    ],
  },
  {
    nome: 'Impostos e taxas',
    natureza: 'despesa',
    categorias: [
      'IRPJ',
      'CSLL',
      'PIS',
      'COFINS',
      'ISS',
      'IRRF sobre serviços',
      'SIMPLES Nacional (DAS)',
      'Taxas e emolumentos',
      'Outros - Impostos',
    ],
  },
  {
    nome: 'Despesas financeiras',
    natureza: 'despesa',
    categorias: [
      'Tarifas bancárias',
      'Juros e encargos',
      'IOF',
      'Antecipação de recebíveis',
      'Outras despesas financeiras',
    ],
  },
  {
    nome: 'Investimentos',
    natureza: 'despesa',
    categorias: [
      'Equipamentos e informática',
      'Mobiliário',
      'Obras e benfeitorias',
      'Aquisição de software',
      'Outros - Investimentos',
    ],
  },
] as const;

/** Lista plana, na ordem dos grupos — alimenta o seletor e a validacao da API. */
export const CATEGORIES: readonly string[] = PLANO_DE_CONTAS.flatMap(
  (grupo) => grupo.categorias,
);

export type Category = string;

const CATEGORY_SET: ReadonlySet<string> = new Set(CATEGORIES);

export function isCategory(value: unknown): value is Category {
  return typeof value === 'string' && CATEGORY_SET.has(value);
}

/** Grupo a que uma categoria pertence, ou `null` se ela saiu do plano. */
export function grupoDaCategoria(categoria: string): GrupoDeContas | null {
  return PLANO_DE_CONTAS.find((g) => g.categorias.includes(categoria)) ?? null;
}
